import { forwardRef, type HTMLAttributes, type ReactNode } from "react";
import { cn } from "@/lib/utils";

export type SectionTone = "default" | "ink" | "accent" | "paper";

export interface SectionProps extends HTMLAttributes<HTMLElement> {
  tone?: SectionTone;
  padding?: "sm" | "md" | "lg";
  dateline?: ReactNode;
  containerClassName?: string;
  as?: "section" | "header" | "footer" | "div" | "article";
}

const TONE_CLASSES: Record<SectionTone, string> = {
  default: "bg-background text-foreground",
  ink: "bg-ink text-paper",
  accent: "bg-accent-press text-accent-press-foreground",
  paper: "bg-paper text-ink dark:bg-paper dark:text-ink",
};

const PADDING_CLASSES: Record<NonNullable<SectionProps["padding"]>, string> = {
  sm: "py-12 md:py-16",
  md: "py-16 md:py-24",
  lg: "py-24 md:py-32",
};

export const Section = forwardRef<HTMLElement, SectionProps>(function Section(
  { tone = "default", padding = "md", dateline, containerClassName, className, children, as = "section", ...rest },
  ref,
) {
  const Tag = as;
  return (
    <Tag
      ref={ref as never}
      className={cn(TONE_CLASSES[tone], PADDING_CLASSES[padding], className)}
      {...rest}
    >
      <div className={cn("mx-auto w-full max-w-[1200px] px-5 md:px-6", containerClassName)}>
        {dateline ? <div className="dateline mb-6">{dateline}</div> : null}
        {children}
      </div>
    </Tag>
  );
});
